import type { ComponentProps, ReactNode } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import {
  radii,
  spacing,
  useTheme,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import { ButtonContext } from "./button-context";
import { Icon } from "./Icon";
import type { ButtonVariant } from "./types";

export function Button({
  children,
  icon,
  iconName,
  iconPosition = "left",
  variant = "primary",
  compact = false,
  disabled,
  align = "stretch",
  style,
  ...props
}: Omit<ComponentProps<typeof Pressable>, "children"> & {
  children?: ReactNode;
  icon?: ReactNode;
  iconName?: ComponentProps<typeof Icon>["name"];
  iconPosition?: "left" | "right";
  variant?: ButtonVariant;
  compact?: boolean;
  align?: "stretch" | "start" | "center";
}) {
  const { colors } = useTheme();
  const { stylesheet, variantStyles, hoverStyles } =
    useThemedStyles(makeStyles);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled: Boolean(disabled) }}
      disabled={disabled}
      style={(state) => [
        stylesheet.button,
        compact ? stylesheet.compact : null,
        variantStyles[variant],
        state.hovered || state.pressed ? hoverStyles[variant] : null,
        align === "start" ? stylesheet.alignStart : null,
        align === "center" ? stylesheet.alignCenter : null,
        disabled ? stylesheet.disabled : null,
        typeof style === "function" ? style(state) : style,
      ]}
      {...props}
    >
      <ButtonContext.Provider value={variant}>
        <View
          style={[
            stylesheet.content,
            variant === "text" ? null : stylesheet.contentCentered,
          ]}
        >
          {iconPosition === "left" ? icon : null}
          {iconPosition === "left" && iconName ? (
            <Icon
              name={iconName}
              size={17}
              color={iconColorForVariant(variant, colors)}
            />
          ) : null}
          {children}
          {iconPosition === "right" && iconName ? (
            <Icon
              name={iconName}
              size={17}
              color={iconColorForVariant(variant, colors)}
            />
          ) : null}
          {iconPosition === "right" ? icon : null}
        </View>
      </ButtonContext.Provider>
    </Pressable>
  );
}

const makeStyles = (colors: ThemeColors) => {
  const stylesheet = StyleSheet.create({
    button: {
      minHeight: 44,
      justifyContent: "center",
      paddingHorizontal: spacing.m,
      paddingVertical: spacing.s,
      borderRadius: radii.pill,
    },
    compact: { minHeight: 34, paddingVertical: spacing.xs },
    primary: { backgroundColor: colors.primary },
    outline: {
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.background,
    },
    danger: {
      borderWidth: 1,
      borderColor: colors.danger,
      backgroundColor: colors.background,
    },
    text: { backgroundColor: "transparent" },
    link: { backgroundColor: "transparent" },
    primaryHover: { backgroundColor: colors.primaryHover },
    neutralHover: { backgroundColor: colors.surface },
    dangerHover: { backgroundColor: colors.dangerSurface },
    content: { flexDirection: "row", alignItems: "center", gap: spacing.s },
    contentCentered: { justifyContent: "center" },
    alignStart: { alignSelf: "flex-start" },
    alignCenter: { alignItems: "center" },
    disabled: { opacity: 0.5 },
  });
  const variantStyles = {
    primary: stylesheet.primary,
    outline: stylesheet.outline,
    danger: stylesheet.danger,
    text: stylesheet.text,
    link: stylesheet.link,
  };
  const hoverStyles = {
    primary: stylesheet.primaryHover,
    outline: stylesheet.neutralHover,
    danger: stylesheet.dangerHover,
    text: stylesheet.neutralHover,
    link: stylesheet.neutralHover,
  };
  return { stylesheet, variantStyles, hoverStyles };
};
function iconColorForVariant(variant: ButtonVariant, colors: ThemeColors) {
  if (variant === "primary" || variant === "link") {
    return colors.primaryForeground;
  }
  return variant === "danger" ? colors.danger : colors.heading;
}
